import { NgModule } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Angular Material Imports
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

// About Page Components
import { AboutPageMainContentComponent } from './modules/about/about-page-main-content/about-page-main-content.component';
import { FeedbackPageComponent } from './modules/feedback/feedback-page/feedback-page.component';

// Admin Support Page Components
import { AprGraduationSummaryInfoContentComponent } from './modules/admin-support/apr-graduation-summary/apr-graduation-summary-info-content/apr-graduation-summary-info-content.component';
import { EntryExitFlowInfoContentComponent } from './modules/admin-support/entry-exit/entry-exit-flow-info-content/entry-exit-flow-info-content.component';

// Courses Explorer Page Components
import { CourseCombinationsContentComponent } from './modules/success-metrics/course-combinations/course-combinations-content/course-combinations-content.component';
import { CourseRankContentComponent } from './modules/success-metrics/course-rank/course-rank-content/course-rank-content.component';
import { PrerequisiteChartComponent } from './modules/success-metrics/prerequisite-impact/prerequisite-chart/prerequisite-chart.component';
import { PrerequisitesContentComponent } from './modules/success-metrics/prerequisite-impact/prerequisites-content/prerequisites-content.component';
import { ProgramBottleneckContentComponent } from './modules/success-metrics/program-bottleneck-courses/program-bottleneck-content/program-bottleneck-content.component';

// Courses Recommender System Page Components
import { CoursesRecommenderContentComponent } from './modules/success-metrics/course-recommender/courses-recommender-content/courses-recommender-content.component';

// Financial Aid Page Components
import { FinancialAidMainContentComponent } from './modules/financial-aid/financial-aid-main-content/financial-aid-main-content.component';
import { FinancialAidToolContentComponent } from './modules/financial-aid/financial-aid-tool-content/financial-aid-tool-content.component';

// Form Components
import { ToolFormComponent } from './shared/components/tool-form/tool-form.component';
import { AprGraduationSummaryFormComponent } from './modules/admin-support/apr-graduation-summary/apr-graduation-summary-form/apr-graduation-summary-form.component';
import { AtRiskStudentPopupFormComponent } from './modules/success-metrics/at-risk-student/at-risk-student-popup-form/at-risk-student-popup-form.component';
import { CohortFormComponent } from './modules/success-metrics/cohort-profile/cohort-form/cohort-form.component';
import { CourseCombinationsFormComponent } from './modules/success-metrics/course-combinations/course-combinations-form/course-combinations-form.component';
import { CourseGradeEstimationFormComponent } from './modules/success-metrics/course-grade-estimation/course-grade-estimation-form/course-grade-estimation-form.component';
import { CourseRankFormComponent } from './modules/success-metrics/course-rank/course-rank-form/course-rank-form.component';
import { CourseRecommenderFormComponent } from './modules/success-metrics/course-recommender/course-recommender-form/course-recommender-form.component';
import { EntryExitFlowFormComponent } from './modules/admin-support/entry-exit/entry-exit-flow-form/entry-exit-flow-form.component';
import { FinancialAidFormComponent } from './modules/financial-aid/financial-aid-form/financial-aid-form.component';
import { LoginFormComponent } from './modules/auth/login-form/login-form.component';
import { PrerequisitesFormComponent } from './modules/success-metrics/prerequisite-impact/prerequisites-form/prerequisites-form.component';
import { ProgramBottleneckFormComponent } from './modules/success-metrics/program-bottleneck-courses/program-bottleneck-form/program-bottleneck-form.component';
import { StudentFormComponent } from './modules/success-metrics/student-profile/student-form/student-form.component';

// Reusable components
import { ToolDrawerContainerComponent } from './shared/components/tool-drawer-container/tool-drawer-container.component';
import { ToolHeaderComponent } from './shared/components/tool-header/tool-header.component';
import { ProgressBarComponent } from './shared/components/progress-bar/progress-bar.component';
import { RelatedLinksComponent } from './shared/components/related-links/related-links.component';
import { InstructionStepLinkComponent } from './shared/components/instruction-step-link/instruction-step-link.component';
import { BaseTrackingComponent } from './shared/components/base-tracking/base-tracking.component';
import { ToolboxMenuComponent } from './shared/components/toolbox-menu/toolbox-menu.component';

// General Components
import { FooterComponent } from './shared/components/footer/footer.component';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { UserDialogComponent } from './modules/users/user-dialog/user-dialog.component';
import { SvgIconViewerComponent } from './shared/components/svg-icon-viewer/svg-icon-viewer.component';
import { BackButtonComponent } from './shared/components/back-button/back-button.component';

// Help Page Components
import { GlossaryOfTermsContentComponent } from './modules/help/glossary-of-terms-content/glossary-of-terms-content.component';
import { HelpPageContentComponent } from './modules/help/help-page-content/help-page-content.component';
import { NavigateSupportContentComponent } from './modules/help/navigation-support-content/navigation-support-content.component';
import { ToolsSupportContentComponent } from './modules/help/tools-support-content/tools-support-content.component';

// Home Page Components
import { AboutPamlabComponent } from './modules/home/about-pamlab/about-pamlab.component';
import { GoalCardComponent } from './shared/components/goal-card/goal-card.component';
import { GoalsListComponent } from './shared/components/goals-list/goals-list.component';

// Landing Page Components
import { PamlabLandingPageComponent } from './modules/marketing/pamlab-landing-page/pamlab-landing-page.component';

// Profile Page Components
import { ProfilePageMainContentComponent } from './modules/users/user-profile/profile-page-main-content/profile-page-main-content.component';

// User Page Components
import { ToolsUsageChartComponent } from './modules/users/tools-usage-chart/tools-usage-chart.component';
import { UserInfoDialogComponent } from './modules/users/user-info-dialog/user-info-dialog.component';

// Success Metrics Page Components
import { AprTrendGraphComponent } from './modules/admin-support/apr-graduation-summary/apr-trend-graph/apr-trend-graph.component';
import { AtRiskStudentContentComponent } from './modules/success-metrics/at-risk-student/at-risk-student-content/at-risk-student-content.component';
import { CohortInfoContentComponent } from './modules/success-metrics/cohort-profile/cohort-info-content/cohort-info-content.component';
import { CourseGradeEstimationContentComponent } from './modules/success-metrics/course-grade-estimation/course-grade-estimation-content/course-grade-estimation-content.component';
import { EnrollmentAndRetentionByFallsGraphComponent } from './modules/admin-support/apr-graduation-summary/enrollment-and-retention-by-falls-graph/enrollment-and-retention-by-falls-graph.component';
import { FirstFallGpaGraphComponent } from './modules/admin-support/apr-graduation-summary/first-fall-gpa-graph/first-fall-gpa-graph.component';
import { FourYearGradRateGraphComponent } from './modules/admin-support/apr-graduation-summary/four-year-grad-rate-graph/four-year-grad-rate-graph.component';
import { ProgramStudentChartComponent } from './modules/success-metrics/student-profile/program-student-chart/program-student-chart.component';
import { StudentInfoContentComponent } from './modules/success-metrics/student-profile/student-info-content/student-info-content.component';
import { StudentsAcademicProgressRateGraphComponent } from './modules/admin-support/apr-graduation-summary/students-academic-progress-rate-graph/students-academic-progress-rate-graph.component';

// Pages Components
import { AboutPageComponent } from './modules/about/about-page/about-page.component';
import { AdvisorIdentifiedAtRiskHelpPageComponent } from './modules/success-metrics/at-risk-student/advisor-identified-at-risk-help-page/advisor-identified-at-risk-help-page.component';
import { AprGraduationSummaryHelpPageComponent } from './modules/admin-support/apr-graduation-summary/apr-graduation-summary-help-page/apr-graduation-summary-help-page.component';
import { AprGraduationSummaryPageComponent } from './modules/admin-support/apr-graduation-summary/apr-graduation-summary-page/apr-graduation-summary-page.component';
import { AtRiskStudentPageComponent } from './modules/success-metrics/at-risk-student/at-risk-student-page/at-risk-student-page.component';
import { CohortInfoPageComponent } from './modules/success-metrics/cohort-profile/cohort-info-page/cohort-info-page.component';
import { CourseCombinationsHelpPageComponent } from './modules/success-metrics/course-combinations/course-combinations-help-page/course-combinations-help-page.component';
import { CourseRankHelpPageComponent } from './modules/success-metrics/course-rank/course-rank-help-page/course-rank-help-page.component';
import { CourseGradeEstimationHelpPageComponent } from './modules/success-metrics/course-grade-estimation/course-grade-estimation-help-page/course-grade-estimation-help-page.component';
import { CourseRecommenderHelpPageComponent } from './modules/success-metrics/course-recommender/course-recommender-help-page/course-recommender-help-page.component';
import { CourseCombinationsPageComponent } from './modules/success-metrics/course-combinations/course-combinations-page/course-combinations-page.component';
import { CourseGradeEstimationPageComponent } from './modules/success-metrics/course-grade-estimation/course-grade-estimation-page/course-grade-estimation-page.component';
import { CourseRankPageComponent } from './modules/success-metrics/course-rank/course-rank-page/course-rank-page.component';
import { CourseRecommenderPageComponent } from './modules/success-metrics/course-recommender/course-recommender-page/course-recommender-page.component';
import { EntryExitFlowPageComponent } from './modules/admin-support/entry-exit/entry-exit-flow-page/entry-exit-flow-page.component';
import { FinancialAidHelpPageComponent } from './modules/financial-aid/financial-aid-help-page/financial-aid-help-page.component';
import { FinancialAidToolHelpPageComponent } from './modules/financial-aid/financial-aid-tool-help-page/financial-aid-tool-help-page.component';
import { FinancialAidToolPageComponent } from './modules/financial-aid/financial-aid-tool-page/financial-aid-tool-page.component';
import { GlossaryOfTermsPageComponent } from './modules/help/glossary-of-terms-page/glossary-of-terms-page.component';
import { HelpHomepageComponent } from './modules/help/help-homepage/help-homepage.component';
import { LoginPageComponent } from './modules/auth/login-page/login-page.component';
import { NavigateSupportPageComponent } from './modules/help/navigation-support-page/navigation-support-page.component';
import { NewApplicantsHelpPageComponent } from './modules/admin-support/new-applicants/new-applicants-help-page/new-applicants-help-page.component';
import { PrerequisitesPageComponent } from './modules/success-metrics/prerequisite-impact/prerequisites-page/prerequisites-page.component';
import { PrerequisiteImpactHelpPageComponent } from './modules/success-metrics/prerequisite-impact/prerequisite-impact-help-page/prerequisite-impact-help-page.component';
import { ProgramBottleneckCoursesHelpPageComponent } from './modules/success-metrics/program-bottleneck-courses/program-bottleneck-courses-help-page/program-bottleneck-courses-help-page.component';
import { ProfilePageComponent } from './modules/users/user-profile/profile-page/profile-page.component';
import { ProgramBottleneckPageComponent } from './modules/success-metrics/program-bottleneck-courses/program-bottleneck-page/program-bottleneck-page.component';
import { RegisterComponent } from './modules/auth/register/register.component';
import { StudentInfoPageComponent } from './modules/success-metrics/student-profile/student-info-page/student-info-page.component';
import { UsersContentComponent } from './modules/users/users-content/users-content.';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { ToolsSupportPageComponent } from './modules/help/tools-support-page/tools-support-page.component';
import { TransferEnrollmentTrendsExternalHelpPageComponent } from './modules/admin-support/transfer-enrollment-trends/transfer-enrollment-trends-external-help-page/transfer-enrollment-trends-external-help-page.component';
import { ToolFormActionButtonComponent } from './shared/components/tool-form-action-button/tool-form-action-button.component';
import { FticCourseDwfAprHelpPageComponent } from './modules/admin-support/ftic-course-dwf-apr/ftic-course-dwf-apr-help-page/ftic-course-dwf-apr-help-page.component';
import { EntryExitFlowExternalHelpPageComponent } from './modules/admin-support/entry-exit/entry-exit-flow-external-help-page/entry-exit-flow-external-help-page.component';
import { ThreeYearGraduationRateHelpPageComponent } from './modules/admin-support/three-year-graduation-rate/three-year-graduation-rate-help-page/three-year-graduation-rate-help-page.component';
import { AprGraduationSummaryExternalHelpPageComponent } from './modules/admin-support/apr-graduation-summary/apr-graduation-summary-external-help-page/apr-graduation-summary-external-help-page.component';
import { FticCourseDwfAprExternalHelpPageComponent } from './modules/admin-support/ftic-course-dwf-apr/ftic-course-dwf-apr-external-help-page/ftic-course-dwf-apr-external-help-page.component';
import { CriticalCourseScoreHelpPageComponent } from './modules/admin-support/critical-course-score/critical-course-score-help-page/critical-course-score-help-page.component';
import { CohortProfileHelpPageComponent } from './modules/success-metrics/cohort-profile/cohort-profile-help-page/cohort-profile-help-page.component';
import { StudentProfileHelpPageComponent } from './modules/success-metrics/student-profile/student-profile-help-page/student-profile-help-page.component';
import { EntryExitFlowHelpPageComponent } from './modules/admin-support/entry-exit/entry-exit-flow-help-page/entry-exit-flow-help-page.component';

import { TransferEnrollmentTrendsFormComponent } from './modules/admin-support/transfer-enrollment-trends/transfer-enrollment-trends-form/transfer-enrollment-trends-form.component';
import { TransferEnrollmentTrendsContentComponent } from './modules/admin-support/transfer-enrollment-trends/transfer-enrollment-trends-content/transfer-enrollment-trends-content.component';
import { TransferEnrollmentTrendsPageComponent } from './modules/admin-support/transfer-enrollment-trends/transfer-enrollment-trends-page/transfer-enrollment-trends-page.component';
import { FticCourseDwfAprPageComponent } from './modules/admin-support/ftic-course-dwf-apr/ftic-course-dwf-apr-page/ftic-course-dwf-apr-page.component';
import { FticCourseDwfAprFormComponent } from './modules/admin-support/ftic-course-dwf-apr/ftic-course-dwf-apr-form/ftic-course-dwf-apr-form.component';
import { CoursesAnalysisContentComponent } from './modules/admin-support/ftic-course-dwf-apr/ftic-course-dwf-apr-content/ftic-course-dwf-apr-content.component';
import { TransferEnrollmentTrendsHelpPageComponent } from './modules/admin-support/transfer-enrollment-trends/transfer-enrollment-trends-help-page/transfer-enrollment-trends-help-page.component';
import { NavPageLinkComponent } from './shared/components/nav-page-link/nav-page-link.component';
import { ContactUsComponent } from './shared/components/contact-us/contact-us.component';
import { InfoPageComponent } from './modules/info-main-page/info-page/info-page.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginFormComponent,
    NavbarComponent,
    FooterComponent,
    GoalCardComponent,
    GoalsListComponent,
    AboutPamlabComponent,
    LoginPageComponent,
    CourseCombinationsContentComponent,
    PrerequisitesContentComponent,
    CoursesRecommenderContentComponent,
    PrerequisitesPageComponent,
    CourseCombinationsFormComponent,
    CourseCombinationsPageComponent,
    CourseRecommenderPageComponent,
    CourseRecommenderFormComponent,
    PrerequisitesFormComponent,
    FinancialAidMainContentComponent,
    StudentInfoContentComponent,
    StudentInfoPageComponent,
    StudentFormComponent,
    PrerequisiteChartComponent,
    CohortFormComponent,
    ProgramStudentChartComponent,
    CohortInfoContentComponent,
    CohortInfoPageComponent,
    FinancialAidFormComponent,
    FinancialAidToolPageComponent,
    FinancialAidToolContentComponent,
    CourseGradeEstimationFormComponent,
    CourseGradeEstimationPageComponent,
    RegisterComponent,
    ProfilePageComponent,
    ProfilePageMainContentComponent,
    AboutPageMainContentComponent,
    AboutPageComponent,
    UsersContentComponent,
    UserDialogComponent,
    ProgramBottleneckFormComponent,
    ProgramBottleneckPageComponent,
    ProgramBottleneckContentComponent,
    CourseGradeEstimationContentComponent,
    ToolsUsageChartComponent,
    UserInfoDialogComponent,
    AtRiskStudentPageComponent,
    AtRiskStudentContentComponent,
    AtRiskStudentPopupFormComponent,
    ConfirmDialogComponent,
    CourseRankContentComponent,
    CourseRankPageComponent,
    CourseRankFormComponent,
    PamlabLandingPageComponent,
    AprGraduationSummaryPageComponent,
    AprGraduationSummaryFormComponent,
    AprGraduationSummaryInfoContentComponent,
    AprTrendGraphComponent,
    EnrollmentAndRetentionByFallsGraphComponent,
    FirstFallGpaGraphComponent,
    FourYearGradRateGraphComponent,
    StudentsAcademicProgressRateGraphComponent,
    EntryExitFlowInfoContentComponent,
    EntryExitFlowFormComponent,
    EntryExitFlowPageComponent,
    HelpHomepageComponent,
    NavigateSupportPageComponent,
    ToolsSupportPageComponent,
    GlossaryOfTermsPageComponent,
    GlossaryOfTermsContentComponent,
    HelpPageContentComponent,
    NavigateSupportContentComponent,
    ToolsSupportContentComponent,
    AdvisorIdentifiedAtRiskHelpPageComponent,
    CourseCombinationsHelpPageComponent,
    CourseRankHelpPageComponent,
    PrerequisiteImpactHelpPageComponent,
    ProgramBottleneckCoursesHelpPageComponent,
    CourseRecommenderHelpPageComponent,
    CourseGradeEstimationHelpPageComponent,
    FinancialAidHelpPageComponent,
    FinancialAidToolHelpPageComponent,
    AprGraduationSummaryHelpPageComponent,
    NewApplicantsHelpPageComponent,
    TransferEnrollmentTrendsExternalHelpPageComponent,
    SvgIconViewerComponent,
    ToolDrawerContainerComponent,
    ToolHeaderComponent,
    ToolFormComponent,
    ToolFormActionButtonComponent,
    ProgressBarComponent,
    FticCourseDwfAprPageComponent,
    FticCourseDwfAprFormComponent,
    CoursesAnalysisContentComponent,
    FticCourseDwfAprHelpPageComponent,
    EntryExitFlowExternalHelpPageComponent,
    ThreeYearGraduationRateHelpPageComponent,
    AprGraduationSummaryExternalHelpPageComponent,
    FticCourseDwfAprExternalHelpPageComponent,
    CriticalCourseScoreHelpPageComponent,
    CohortProfileHelpPageComponent,
    BackButtonComponent,
    StudentProfileHelpPageComponent,
    EntryExitFlowHelpPageComponent,
    RelatedLinksComponent,
    InstructionStepLinkComponent,
    TransferEnrollmentTrendsFormComponent,
    TransferEnrollmentTrendsContentComponent,
    TransferEnrollmentTrendsPageComponent,
    TransferEnrollmentTrendsHelpPageComponent,
    NavPageLinkComponent,
    ContactUsComponent,
    InfoPageComponent,
    BaseTrackingComponent,
    ToolboxMenuComponent,
    FeedbackPageComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    FormsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    ScrollingModule,
    MatTableModule,
    MatListModule,
    MatProgressBarModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatSortModule,
    MatToolbarModule,
    MatPaginatorModule,
    MatGridListModule,
    MatDividerModule,
    MatCardModule,
    BrowserAnimationsModule,
    MatButtonToggleModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    // NgxSliderModule,
    // NgChartsModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTabsModule,
    MatExpansionModule,
    MatRadioModule,
    MatSliderModule,
    MatSidenavModule,
    MatDatepickerModule,
  ],
  providers: [
    provideClientHydration(),
    provideHttpClient(withFetch()),
    provideHttpClient(withInterceptorsFromDi()),
    provideNativeDateAdapter(),
  ],
})
export class AppModule {}
